/**
 * @module makeStyles
 *
 * Build a `useStyles` hook from a CSS-in-JS object.
 *
 * @see https://docs.tss-react.dev/api/makestyles
 */
import type { Theme } from '@mui/material/styles';
import type { CSSObject } from 'tss-react';
// Imported from `tss-react/mui` rather than `tss-react` so it shares MUI's own Emotion cache.
// On a separate cache the two compete on document order, and a wrapper's styles can lose to the
// MUI component it decorates.
import { makeStyles as createUseStyles } from 'tss-react/mui';

type StylesByRuleName<RuleName extends string> = Record<RuleName, CSSObject>;

/**
 * Wraps tss-react's `makeStyles` so that the call is uncurried and `useStyles`
 * resolves to the class names alone rather than `{ classes, cx, css, theme }`.
 * Several hundred call sites across the app are written against that shape.
 *
 * Import `makeStyles` from `tss-react/mui` directly for the parts this drops:
 * style params, `cx`, or a nested selector that references another rule.
 */
export const makeStyles = <RuleName extends string>(
  styles: StylesByRuleName<RuleName> | ((theme: Theme) => StylesByRuleName<RuleName>),
): (() => Record<RuleName, string>) => {
  const useStyles = createUseStyles()(styles);

  return () => useStyles().classes;
};
